import {
  InputLabel,
  createFilterOptions,
  AutocompleteProps,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  FormHelperText,
} from '@mui/material';
import { forwardRef, useMemo } from 'react';

import { useComboBoxLocators } from './hooks/useComboBoxLocators';
import { StyledAutocomplete } from './styledComponents/StyledAutocomplete';
import { StyledFormControl } from './styledComponents/StyledFormControl';
import type { InputComponentWidth } from '../../types';
import { StyledChip } from './styledComponents/StyledChip';
import { StyledTextField } from './styledComponents/StyledTextField';
import { getWidth } from '../../utils/getWidth';
import Checkbox from '../Checkbox/Checkbox';
import { getDropdownItemDisplayValue, getLabelWithIcon } from '../Dropdown/helpers';
import {
  ViewModeContainer,
  ViewModeLabel,
  ViewModeValue,
} from '../Dropdown/styledComponents/ViewMode';
import { type DropdownListItem } from '../Dropdown/types';
import { Tooltip } from '../Tooltip/Tooltip';
import { StyledOption } from './styledComponents/StyledOption';

export type ComboBoxProps = {
  /**
   * A unique identifier for the combo-box component.
   * Used to distinguish this combo-box from others in the application.
   */
  id: string;

  /**
   * Label of the input
   */
  label: string;

  /**
   * Whether the input is mandatory
   */
  required?: boolean;

  /**
   * Width of the input
   * Can accept predefined sizes like 'large', 'small', etc., or a custom number value.
   * @default 'medium'
   */
  width?: InputComponentWidth;

  /**
   * The placeholder text
   * @default 'Select value from list'
   */
  placeholder?: string;

  /**
   * Show the selected value in the tooltip
   * @default false
   */
  showTooltip?: boolean;

  /**
   * To display the ComboBox in the view mode
   * @default false
   * */
  viewMode?: boolean;

  /**
   * Error message to display below the field
   * When provided, the field will show error state
   */
  fieldError?: string;

  /**
   * Whether to show a checkbox for the input
   * @default false
   */
  showCheckbox?: boolean;

  /**
   * Whether to show an icon for the input
   * @default false
   */
  showIcon?: boolean;
} & Omit<AutocompleteProps<DropdownListItem, boolean, boolean, undefined>, 'renderInput'>;

export const ComboBox = forwardRef<HTMLInputElement, ComboBoxProps>((props, ref) => {
  const {
    id,

    value: selected,
    options = [],
    disabled = false,

    label,
    required,
    width = 'medium',
    placeholder,
    showTooltip = false,
    viewMode = false,
    fieldError,
    showCheckbox = false,
    showIcon = false,
    ...rest
  } = props;

  const {
    formControlNodeId,
    viewModeContainerNodeId,
    labelNodeId,
    comboBoxInputNodeId,
    chipNodeId,
    ariaComboBoxLabel,
  } = useComboBoxLocators({ key: id });

  const optionsWithKey = useMemo(() => {
    return options.map((o) => ({ ...o, key: o.value }));
  }, [options]);

  const filterOptions = createFilterOptions<DropdownListItem>({
    matchFrom: 'any',
    ignoreCase: true,
  });

  const customLabel = label + (required ? ' *' : '');
  const viewModeValue = selected
    ? Array.isArray(selected)
      ? selected.map((v) => getDropdownItemDisplayValue(v)).join(', ')
      : getDropdownItemDisplayValue(selected)
    : undefined;

  const renderTags = (tagValue: DropdownListItem[], getTagProps: AutocompleteRenderGetTagProps) =>
    tagValue.map((option = {} as DropdownListItem, index) => (
      <StyledChip
        id={`${chipNodeId}-${option.value}`}
        label={getLabelWithIcon(option, { gap: 1, iconSize: 24, showIcon })}
        {...getTagProps({ index })}
        status={option.status}
        key={`combox-chip-${option.value}`}
      />
    ));

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <Tooltip
      id={`comboBox-tooltip-${id}`}
      title={showTooltip ? viewModeValue || '' : ''}
      placement="top"
      arrow
    >
      <StyledTextField
        {...params}
        id={comboBoxInputNodeId}
        placeholder={placeholder}
        error={!!fieldError}
      />
    </Tooltip>
  );

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: string;
    },
    option: DropdownListItem,
    { selected }: { selected: boolean }
  ) => {
    const { key, ...optionProps } = props;

    const displayLabel = getLabelWithIcon(option, { iconSize: 20, gap: 2, showIcon });
    return (
      <StyledOption {...optionProps}>
        {showCheckbox ? (
          <Checkbox
            id={key}
            checked={selected}
            displayLabel={displayLabel}
            size="small"
            disabled={disabled}
          />
        ) : (
          displayLabel
        )}
      </StyledOption>
    );
  };

  return (
    <StyledFormControl
      id={formControlNodeId}
      variant="outlined"
      sx={{ m: 0, width: getWidth(width) }}
      aria-labelledby={ariaComboBoxLabel}
      error={!!fieldError}
    >
      {viewMode ? (
        <ViewModeContainer id={viewModeContainerNodeId} role="presentation">
          <ViewModeLabel aria-label={ariaComboBoxLabel}>{label}</ViewModeLabel>
          <ViewModeValue aria-labelledby={ariaComboBoxLabel}>{viewModeValue || ''}</ViewModeValue>
        </ViewModeContainer>
      ) : (
        <>
          <InputLabel
            id={labelNodeId}
            htmlFor={comboBoxInputNodeId}
            style={{ color: disabled ? '#d2d4d7' : undefined }}
            aria-label={ariaComboBoxLabel}
            shrink
            children={customLabel}
          />
          <StyledAutocomplete
            autoComplete
            renderTags={renderTags}
            renderInput={renderInput}
            getOptionLabel={getDropdownItemDisplayValue}
            renderOption={renderOption}
            aria-labelledby={ariaComboBoxLabel}
            {...rest}
            ref={ref}
            disabled={disabled}
            value={selected}
            options={optionsWithKey || []}
            filterOptions={filterOptions}
            filterSelectedOptions={!showCheckbox}
            isOptionEqualToValue={(option, value: DropdownListItem) =>
              option?.value === value?.value
            }
            disableCloseOnSelect={showCheckbox}
          />
        </>
      )}

      {fieldError ? <FormHelperText error>{fieldError}</FormHelperText> : null}
    </StyledFormControl>
  );
});
